import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  Stack,
  Text,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { sendEmailVerification } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { useGetUserRole } from 'domains/user/hooks/useGetUserRole';
import { useEmailVerification } from 'utilities/hooks/useEmailVerification';
import { useFirebaseAuth } from 'domains/auth/hooks/useFirebaseAuth';
import { FiArrowLeft, FiCheckCircle, FiMail, FiRefreshCw } from 'react-icons/fi';
import { useTranslation } from 'i18n/TranslationProvider';
import heroIllustration from 'assets/img/auth/auth_verification_hero.png';
import LanguageSelector from 'components/i18n/LanguageSelector';
import { UnifiedAuthPanel } from 'components/layout/UnifiedPrimitives';

const EMAIL_VERIFICATION_POLL_INTERVAL_MS = 15000;

export default function EmailVerification() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { role, loading: roleLoading } = useGetUserRole();
  const toast = useToast();
  const navigate = useNavigate();
  const { markEmailAsVerified } = useEmailVerification();
  const { loading: authLoading } = useFirebaseAuth();
  const [isResending, setIsResending] = useState(false);
  const [isCheckingNow, setIsCheckingNow] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const pageBg = useColorModeValue(
    'linear(to-br, #f8fbff, #eef4ff 35%, #e9f0ff)',
    'linear(to-br, #0f1835, #161f45 45%, #1d2a56)',
  );
  const titleColor = useColorModeValue('navy.700', 'white');
  const textMuted = useColorModeValue('gray.600', 'gray.300');
  const iconBg = useColorModeValue('blue.50', 'whiteAlpha.100');
  const iconColor = useColorModeValue('blue.500', 'blue.300');
  const statusDotColor = useColorModeValue('orange.400', 'orange.300');
  const infoBg = useColorModeValue('rgba(57,101,255,0.06)', 'rgba(57,101,255,0.12)');
  const infoBorder = useColorModeValue('rgba(57,101,255,0.15)', 'rgba(57,101,255,0.25)');
  const panelBorder = useColorModeValue('rgba(255,255,255,0.9)', 'rgba(255,255,255,0.15)');

  const redirectAfterVerification = useCallback(async () => {
    if (!user?.emailVerified || roleLoading) return;
    await markEmailAsVerified();
    if (role === 'admin') {
      navigate('/admin/dashboard');
      return;
    }
    if (role === 'client') {
      navigate('/');
      return;
    }
    navigate('/verification/pending');
  }, [user, role, roleLoading, markEmailAsVerified, navigate]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth/sign-in');
      return;
    }

    if (!user || user.emailVerified) {
      redirectAfterVerification();
      return;
    }

    const checkVerificationStatus = async () => {
      try {
        await user.reload();
        if (user.emailVerified) {
          await redirectAfterVerification();
        }
      } catch (error) {
        console.error('Email verification polling failed:', error);
      }
    };

    checkVerificationStatus();

    const interval = setInterval(checkVerificationStatus, EMAIL_VERIFICATION_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [authLoading, user, navigate, redirectAfterVerification]);

  const handleResendEmail = async () => {
    if (!user || isResending) return;
    setIsResending(true);
    try {
      await sendEmailVerification(user);
      toast({
        title: t('auth.verification_email_sent'),
        description: t('auth.we_sent_a_new_verification_link'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error resending verification email:', error);
      toast({
        title: t('auth.failed_to_resend_email'),
        description: t('auth.unable_to_resend_verification_email_right_now'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleCheckNow = async () => {
    if (!user || isCheckingNow) return;
    setIsCheckingNow(true);
    try {
      await user.reload();
      if (user.emailVerified) {
        toast({
          title: t('auth.email_verified'),
          description: t('auth.redirecting_you_now'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        await redirectAfterVerification();
      } else {
        toast({
          title: t('auth.email_not_verified_yet'),
          description: t('auth.please_click_the_link_in_your_inbox_and_try_again'),
          status: 'info',
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error checking verification status:', error);
      toast({
        title: t('auth.error'),
        description: t('auth.unable_to_check_verification_status_right_now'),
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsCheckingNow(false);
    }
  };

  const handleLogoutToSignIn = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await Promise.resolve(logout?.());
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      setIsLoggingOut(false);
      navigate('/auth/sign-in', { replace: true });
    }
  };

  return (
    <Box position="relative" minH="100dvh" bgGradient={pageBg} overflow="hidden">
      {/* Background orbs */}
      <Box
        position="absolute"
        top="-220px"
        right="-220px"
        w="560px"
        h="560px"
        borderRadius="full"
        bgGradient="radial(brand.300, transparent 68%)"
        opacity={0.35}
        pointerEvents="none"
      />
      <Box
        position="absolute"
        bottom="-260px"
        left="-180px"
        w="520px"
        h="520px"
        borderRadius="full"
        bgGradient="radial(blue.300, transparent 70%)"
        opacity={0.22}
        pointerEvents="none"
      />

      {/* Language selector */}
      <Flex position="absolute" top={{ base: 4, md: 8 }} right={{ base: 4, md: 8 }} zIndex={2}>
        <LanguageSelector size="sm" variant="solid" showLabel />
      </Flex>

      {/* Main layout */}
      <Flex
        position="relative"
        minH="100dvh"
        w="100%"
        px={{ base: 4, sm: 6, md: 8 }}
        pt={{ base: 'calc(env(safe-area-inset-top) + 1rem)', md: 8 }}
        pb={{ base: 'calc(env(safe-area-inset-bottom) + 1rem)', md: 8 }}
        align="center"
        justify="center"
      >
        <Flex
          w="100%"
          maxW={{ base: '100%', sm: '560px', md: '640px', xl: '1100px', '2xl': '1260px' }}
          justify={{ base: 'center', xl: 'space-between' }}
          align="stretch"
          gap={{ xl: 6, '2xl': 12 }}
        >
          {/* Form / Content Side */}
          <Box
            flex={1}
            w="100%"
            maxW={{ base: '100%', sm: '560px', md: '640px' }}
            mx="auto"
            zIndex={1}
            display="flex"
            flexDirection="column"
            justifyContent="center"
          >
            <Box
              w="100%"
              maxW={{ base: '100%', sm: '540px', md: '620px' }}
              mx="auto"
              textAlign={{ base: 'center', sm: 'left' }}
            >
              <UnifiedAuthPanel
                borderRadius={{ base: '24px', md: '30px' }}
                px={{ base: 5, sm: 6, md: 8 }}
                py={{ base: 6, sm: 7, md: 8 }}
              >
                <Stack spacing={{ base: 6, md: 7 }} align="center">
                  {/* Badge */}
                  <Badge
                    colorScheme="blue"
                    borderRadius="full"
                    px={4}
                    py={1.5}
                    fontSize="10px"
                    fontWeight="800"
                    textTransform="uppercase"
                    letterSpacing="0.08em"
                    variant="subtle"
                    bg={useColorModeValue('blue.50', 'rgba(57,101,255,0.15)')}
                    color={useColorModeValue('blue.600', 'blue.200')}
                    border="1px solid"
                    borderColor={useColorModeValue('blue.100', 'rgba(57,101,255,0.2)')}
                  >
                    {t('auth.email_verification_required')}
                  </Badge>

                  {/* Icon + Title */}
                  <VStack spacing={4} align="center" w="100%">
                    <Box
                      w={16}
                      h={16}
                      borderRadius="20px"
                      bg={iconBg}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      flexShrink={0}
                      boxShadow="0 8px 24px rgba(57,101,255,0.18)"
                      transform="rotate(-3deg)"
                    >
                      <Icon as={FiMail} color={iconColor} boxSize={8} />
                    </Box>
                    <Heading
                      color={titleColor}
                      fontSize={{ base: '28px', md: '36px' }}
                      lineHeight="1.1"
                      fontWeight="800"
                      letterSpacing="-0.02em"
                    >
                      {t('auth.check_your_email')}
                    </Heading>
                  </VStack>

                  {/* Status row - Improved with animation and better border */}
                  <HStack
                    spacing={3}
                    align="center"
                    px={4}
                    py={3}
                    borderRadius="14px"
                    bg={infoBg}
                    border="1px solid"
                    borderColor={infoBorder}
                    w="100%"
                    justify="center"
                  >
                    <Box
                      w="10px"
                      h="10px"
                      borderRadius="full"
                      bg={statusDotColor}
                      flexShrink={0}
                      boxShadow={`0 0 12px ${statusDotColor}`}
                      style={{ animation: 'pulse 2s cubic-bezier(0.4,0,0.6,1) infinite' }}
                    />
                    <Text color={textMuted} fontSize="sm" fontWeight="600" letterSpacing="0.01em">
                      {t('auth.waiting_for_email_verification')}
                    </Text>
                    <style>{`@keyframes pulse { 0%,100%{opacity:1; transform: scale(1);} 50%{opacity:0.4; transform: scale(0.9);} }`}</style>
                  </HStack>

                  {/* Description */}
                  <Text
                    color={textMuted}
                    fontSize={{ base: '15px', md: '17px' }}
                    lineHeight="1.6"
                    maxW="100%"
                  >
                    {t('auth.we_sent_your_verification_link_to')}{' '}
                    <Box as="span" fontWeight="800" color={titleColor} borderBottom="2px solid" borderColor="blue.400" pb="1px">
                      {user?.email}
                    </Box>
                    .{' '}
                    <Box as="span" opacity={0.8}>
                      {t('auth.didnt_receive_it_check_spam_or_try_again')}
                    </Box>
                  </Text>

                  {/* Action buttons - Improved hierarchy and mobile layout */}
                  <Stack 
                    direction={{ base: 'column', lg: 'row' }} 
                    spacing={{ base: 5, lg: 4 }} 
                    justify="center"
                    w="100%"
                    pt={2}
                  >
                    <Button
                      leftIcon={<FiCheckCircle />}
                      variant="brand"
                      onClick={handleCheckNow}
                      isLoading={isCheckingNow || roleLoading}
                      isDisabled={!user}
                      flex="none"
                      h={{ base: '60px', lg: '50px' }}
                      fontSize={{ base: 'lg', lg: 'md' }}
                      fontWeight="800"
                      boxShadow="0 10px 20px rgba(57,101,255,0.25)"
                      _hover={{ transform: 'translateY(-2px)', boxShadow: '0 12px 24px rgba(57,101,255,0.3)' }}
                      _active={{ transform: 'translateY(0)' }}
                      transition="all 0.2s"
                    >
                      {t('auth.i_verified_my_email')}
                    </Button>
                    <Button
                      leftIcon={<FiRefreshCw />}
                      variant="outline"
                      onClick={handleResendEmail}
                      isLoading={isResending}
                      isDisabled={!user}
                      flex="none"
                      h={{ base: '60px', lg: '50px' }}
                      fontSize={{ base: 'lg', lg: 'md' }}
                      fontWeight="700"
                      borderColor={infoBorder}
                      _hover={{ bg: infoBg, borderColor: 'blue.300' }}
                    >
                      {t('auth.resend_email')}
                    </Button>
                  </Stack>

                  <Button
                    leftIcon={<FiArrowLeft />}
                    variant="ghost"
                    onClick={handleLogoutToSignIn}
                    isLoading={isLoggingOut}
                    size="md"
                    h={{ base: '48px', lg: '40px' }}
                    color={textMuted}
                    fontWeight="600"
                    alignSelf="center"
                    _hover={{ bg: 'transparent', color: titleColor, textDecoration: 'underline' }}
                  >
                    {t('auth.back_to_sign_in')}
                  </Button>
                </Stack>
              </UnifiedAuthPanel>

              {/* Info card - Stylized as a "Pro Tip" */}
              <UnifiedAuthPanel
                mt={6}
                borderRadius="24px"
                px={{ base: 5, sm: 6 }}
                py={{ base: 4, sm: 5 }}
                boxShadow="0 12px 30px rgba(9, 30, 66, 0.08)"
              >
                <HStack spacing={4} align="center">
                  <Box
                    w={10}
                    h={10}
                    borderRadius="12px"
                    bg={useColorModeValue('green.50', 'rgba(72,187,120,0.12)')}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <Icon as={FiCheckCircle} color={useColorModeValue('green.500', 'green.300')} boxSize={5} />
                  </Box>
                  <Text color={textMuted} fontSize="sm" fontWeight="500" lineHeight="1.5">
                    {t('auth.we_automatically_check_every_15_seconds')}
                  </Text>
                </HStack>
              </UnifiedAuthPanel>
            </Box>
          </Box>

          {/* Illustration Side - matches Default.js layout */}
          <Box
            flex={1}
            display={{ base: 'none', xl: 'flex' }}
            w="100%"
            maxW={{ base: '100%', sm: '560px', md: '640px' }}
            mx="auto"
            opacity={0.95}
            pointerEvents="none"
          >
            <Flex
              position="relative"
              w="100%"
              h="100%"
              minH="500px"
              overflow="hidden"
              borderRadius={{ base: '22px', md: '28px' }}
              border="1px solid"
              borderColor={panelBorder}
              bg="#142B76"
              boxShadow="0 24px 70px rgba(11, 31, 77, 0.20)"
              align="center"
              justify="center"
              flexDirection="column"
            >
              <Box
                position="absolute"
                inset={0}
                bgImage={`url(${heroIllustration})`}
                bgSize="cover"
                bgPosition="center"
                bgRepeat="no-repeat"
              />
              <Box
                position="absolute"
                inset={0}
                bgGradient="linear(to-b, rgba(8,24,66,0.02), rgba(8,24,66,0.45))"
              />
              {/* Overlay text */}
              <VStack
                position="relative"
                zIndex={1}
                spacing={3}
                px={8}
                pb={10}
                mt="auto"
              >
                <Badge
                  colorScheme="blue"
                  borderRadius="full"
                  px={4}
                  py={1.5}
                  fontSize="xs"
                  textTransform="uppercase"
                  letterSpacing="widest"
                  bg="rgba(57,101,255,0.25)"
                  color="blue.100"
                  border="1px solid rgba(57,101,255,0.35)"
                >
                  {t('auth.logiflow_platform')}
                </Badge>
                <Heading
                  color="white"
                  fontSize="3xl"
                  textAlign="center"
                  lineHeight="1.2"
                  fontWeight="800"
                  textShadow="0 2px 20px rgba(0,0,0,0.4)"
                >
                  {t('auth.one_step_away_heading')}
                </Heading>
                <Text
                  color="whiteAlpha.700"
                  fontSize="sm"
                  textAlign="center"
                  maxW="36ch"
                >
                  {t('auth.verify_email_subline')}
                </Text>
              </VStack>
            </Flex>
          </Box>
        </Flex>
      </Flex>
    </Box>
  );
}
